import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginInput } from '@auctaim/shared';
import { authApi } from '../services/api-client';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // State setters
  setUser: (user: User | null) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;

  // Auth actions
  login: (credentials: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({
        user,
        isAuthenticated: !!user,
        isLoading: false,
      }),

      clearUser: () => set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }),

      setLoading: (isLoading) => set({ isLoading }),

      login: async (credentials: LoginInput) => {
        try {
          set({ isLoading: true });
          const response = await authApi.login(credentials);
          // JWT tokens are stored in HTTP-only cookies by the server
          // We only store the user object in state
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          set({ isLoading: true });
          await authApi.logout();
          // Clear cookies on server side
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          // Even if server logout fails, clear local state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          throw error;
        }
      },

      refreshToken: async () => {
        try {
          // Token refresh happens server-side via HTTP-only cookies
          await authApi.refresh();
          // Optionally fetch updated user info after refresh
          const user = await authApi.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // If refresh fails, clear auth state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          throw error;
        }
      },

      checkAuth: async () => {
        try {
          set({ isLoading: true });
          // Check if user is authenticated by fetching current user
          // This works because JWT is in HTTP-only cookie
          const user = await authApi.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // Not authenticated or token expired
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
      partialize: (state) => ({
        user: state.user,
        // Only persist user data in sessionStorage for state recovery
        // Tokens are in HTTP-only cookies, not stored in storage
      }),
    }
  )
);
